// React Imports
import { useState } from "react";

// Local Components
import { DeveloperProfileForm } from "./DeveloperProfileForm";
import { NavLogo } from "sj-ui-toolkit/src/atlas/components/UserNavBar/NavLogo/NavLogo";

// Hooks & Helpers
import { updateUser } from "sj-ui-toolkit/src/api";
import { useLoginFlow } from "sj-ui-toolkit/src/hooks";

// Constants
import { DeveloperProfileConstants } from "sj-ui-toolkit/src/constants/developerProfile";
import type { AiStack } from "./AiStackSelector";

export interface DevProfileFormData {
  jobFunction: string;
  jobFunctionOther: string;
  experience: string;
  expertise: string;
  language: string;
  additionalLanguages: string[];
  tooling: string[];
  aiStack: AiStack;
  doesNotUseAi: boolean;
}

const emptyAiStack = (): AiStack =>
  Object.fromEntries(
    DeveloperProfileConstants.OPTIONS.aiToolCategories.map(category => [category.key, []])
  );

export default function DeveloperProfilePage() {
  const [loading, setLoading] = useState(false);
  const { handleRedirectAfterLogin } = useLoginFlow();

  const [formData, setFormData] = useState<DevProfileFormData>({
    jobFunction: "",
    jobFunctionOther: "",
    experience: "",
    expertise: "",
    language: "",
    additionalLanguages: [],
    tooling: [],
    aiStack: emptyAiStack(),
    doesNotUseAi: false,
  });

  const handleChange = (field: string, value: unknown) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value } as DevProfileFormData;

      // Reset experience, language, and tooling fields if expertise is "i do not work with code"
      if (
        field === "expertise" &&
        value === DeveloperProfileConstants.VALUES.EXPERTISE_DOES_NOT_WORK_WITH_CODE
      ) {
        next.experience = "";
        next.language = "";
        next.additionalLanguages = [];
        next.tooling = [];
      }

      // "I don't use AI tools" clears any categorized AI selections
      if (field === "doesNotUseAi" && value) {
        next.aiStack = emptyAiStack();
      }

      return next;
    });
  };

  const shouldDisableCodeFields =
    formData.expertise === DeveloperProfileConstants.VALUES.EXPERTISE_DOES_NOT_WORK_WITH_CODE;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const jobFunction =
        formData.jobFunction === DeveloperProfileConstants.VALUES.OTHER
          ? formData.jobFunctionOther
          : formData.jobFunction;

      const payload = {
        developerProfile: {
          experience: formData.experience,
          expertise: formData.expertise,
          jobFunction: jobFunction,
          language: formData.language,
          additionalLanguages: formData.additionalLanguages,
          topics: formData.tooling,
          usesAi: !formData.doesNotUseAi,
          aiStack: formData.aiStack,
        },
      };

      await updateUser(payload);
      setLoading(false);
      handleRedirectAfterLogin();
    } catch (error) {
      console.error("Error saving developer profile:", error);
      setLoading(false);
    }
  };

  return (
    <div className="atlas-bg-home">
      <nav className="px-5 py-3">
        <NavLogo navigateTo={() => {}} />
      </nav>
      <main className="max-w-5xl mx-auto px-5 py-4">
        <div className="flex flex-col gap-2 mb-4">
          <h1 className="atlas-h1">Welcome to Security Journey!</h1>
          <p>
            Before you get started, let's answer a few questions so we can help customize the
            content available to you.
          </p>
        </div>
        <DeveloperProfileForm
          formData={formData}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          loading={loading}
          shouldDisableCodeFields={shouldDisableCodeFields}
        />
      </main>
    </div>
  );
}
